import { addDaysToKey, browserTimeZone, toDateKey } from "./date";
import { hasSupabaseConfig, supabasePublishableKey, supabaseUrl } from "./supabase";
import {
  BookingApiError,
  type AvailableSlot,
  type BookingConfirmation,
  type ManagedBooking,
  type PublicBookingPage,
} from "./types";

type BookingInput = {
  event_slug: string;
  starts_at: string;
  guest_name: string;
  guest_email: string;
  guest_timezone: string;
  guest_notes: string;
  consented_at: string;
  idempotency_key: string;
  turnstile_token?: string;
  company_website?: string;
};

const DEMO_PAGES: Record<string, PublicBookingPage> = {
  "shahil-30": {
    organization: { name: "RSM Consulting", slug: "rsm", timezone: "Africa/Johannesburg" },
    host: {
      slug: "shahil",
      display_name: "Shahil Mathapershad",
      domain_label: "Capital & Credit",
      role: "Quantitative Analyst, ABSA Group",
      bio: "AI-driven wholesale credit analytics and capital decision systems.",
      timezone: "Africa/Johannesburg",
    },
    event_type: {
      slug: "shahil-30",
      title: "30-minute working session",
      description: "Bring the problem. Leave with a clearer next move.",
      duration_minutes: 30,
      minimum_notice_minutes: 120,
      booking_horizon_days: 60,
      location_label: "Online meeting",
    },
  },
  "rashik-30": {
    organization: { name: "RSM Consulting", slug: "rsm", timezone: "Africa/Johannesburg" },
    host: {
      slug: "rashik",
      display_name: "Rashik Singh",
      domain_label: "Operations & Supply",
      role: "Industrial Pharmacist, Medipost Holdings",
      bio: "Warehouse systems, industrial operations, and supply-chain execution.",
      timezone: "Africa/Johannesburg",
    },
    event_type: {
      slug: "rashik-30",
      title: "30-minute working session",
      description: "A focused conversation about the operational problem in front of you.",
      duration_minutes: 30,
      minimum_notice_minutes: 120,
      booking_horizon_days: 60,
      location_label: "Online meeting",
    },
  },
};

function demoSlots(eventSlug: string, rangeStart: string, rangeEnd: string): AvailableSlot[] {
  const results: AvailableSlot[] = [];
  const page = DEMO_PAGES[eventSlug] ?? DEMO_PAGES["shahil-30"]!;
  let cursor = rangeStart;
  while (cursor <= rangeEnd) {
    const weekday = new Date(`${cursor}T12:00:00Z`).getUTCDay();
    const allowed = eventSlug === "rashik-30" || (weekday >= 1 && weekday <= 5);
    if (allowed) {
      const hours = eventSlug === "rashik-30" ? [9, 10, 11, 14, 15, 18] : [9, 10, 11, 14, 15];
      for (const hour of hours) {
        const starts = new Date(`${cursor}T${String(hour).padStart(2, "0")}:00:00+02:00`);
        if (starts.getTime() > Date.now() + page.event_type.minimum_notice_minutes * 60_000) {
          results.push({
            starts_at: starts.toISOString(),
            ends_at: new Date(starts.getTime() + page.event_type.duration_minutes * 60_000).toISOString(),
          });
        }
      }
    }
    cursor = addDaysToKey(cursor, 1);
  }
  return results;
}

async function request<T>(path: string, init?: RequestInit) {
  if (!supabaseUrl || !supabasePublishableKey) {
    throw new BookingApiError("Supabase is not configured.", "configuration_error", 503);
  }
  const response = await fetch(`${supabaseUrl}/functions/v1/booking-api${path}`, {
    ...init,
    headers: {
      apikey: supabasePublishableKey,
      Authorization: `Bearer ${supabasePublishableKey}`,
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });
  const payload = (await response.json().catch(() => null)) as
    | { data: T }
    | { error: { code?: string; message?: string } }
    | null;
  if (!response.ok) {
    const apiError = payload && "error" in payload ? payload.error : undefined;
    const code = apiError?.code;
    const knownCode = code === "slot_unavailable"
      ? "slot_unavailable"
      : code === "event_not_found" || code === "booking_not_found" || code === "route_not_found"
        ? "not_found"
        : code === "rate_limited"
          ? "rate_limited"
          : code?.startsWith("turnstile_")
            ? "verification_failed"
            : response.status >= 400 && response.status < 500
              ? "invalid_request"
              : "server_error";
    throw new BookingApiError(
      apiError?.message ?? "The booking service could not complete this request.",
      knownCode,
      response.status,
    );
  }
  if (!payload || !("data" in payload)) throw new BookingApiError("The booking service returned no data.", "server_error", 502);
  return payload.data;
}

export async function getPublicBookingPage(eventSlug: string) {
  if (!hasSupabaseConfig) {
    const page = DEMO_PAGES[eventSlug];
    if (!page) throw new BookingApiError("This booking page does not exist.", "not_found", 404);
    return page;
  }
  return request<PublicBookingPage>(`/metadata?event_slug=${encodeURIComponent(eventSlug)}`);
}

export async function getAvailability(eventSlug: string, rangeStart: string, rangeEnd: string) {
  if (!hasSupabaseConfig) return demoSlots(eventSlug, rangeStart, rangeEnd);
  const params = new URLSearchParams({ event_slug: eventSlug, range_start: rangeStart, range_end: rangeEnd });
  return request<AvailableSlot[]>(`/availability?${params.toString()}`);
}

export async function createBooking(input: BookingInput) {
  if (!hasSupabaseConfig) {
    const page = DEMO_PAGES[input.event_slug];
    if (!page) throw new BookingApiError("This booking page does not exist.", "not_found", 404);
    const idempotencyStorageKey = `rsm-booking-idempotency:${input.event_slug}:${input.idempotency_key}`;
    const existingConfirmation = localStorage.getItem(idempotencyStorageKey);
    if (existingConfirmation) return JSON.parse(existingConfirmation) as BookingConfirmation;
    const start = new Date(input.starts_at);
    const confirmation: BookingConfirmation = {
      booking_id: crypto.randomUUID(),
      reference: `RSM-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
      event_slug: input.event_slug,
      manage_token: crypto.randomUUID().replaceAll("-", ""),
      starts_at: start.toISOString(),
      ends_at: new Date(start.getTime() + page.event_type.duration_minutes * 60_000).toISOString(),
      title: page.event_type.title,
      host_name: page.host.display_name,
      guest_name: input.guest_name,
      guest_email: input.guest_email,
      guest_timezone: input.guest_timezone,
      location_label: page.event_type.location_label,
      meeting_url: null,
    };
    const managedBooking: ManagedBooking = {
      ...confirmation,
      status: "confirmed",
      guest_notes: input.guest_notes || null,
      cancelled_at: null,
    };
    localStorage.setItem(`rsm-booking:${confirmation.manage_token}`, JSON.stringify(managedBooking));
    localStorage.setItem(idempotencyStorageKey, JSON.stringify(confirmation));
    await new Promise((resolve) => window.setTimeout(resolve, 450));
    return confirmation;
  }
  return request<BookingConfirmation>("/book", { method: "POST", body: JSON.stringify(input) });
}

export async function getManagedBooking(token: string) {
  if (!hasSupabaseConfig) {
    const stored = localStorage.getItem(`rsm-booking:${token}`);
    if (!stored) throw new BookingApiError("This booking link is no longer valid.", "not_found", 404);
    const parsed = JSON.parse(stored) as BookingConfirmation | ManagedBooking;
    const managed: ManagedBooking = {
      ...parsed,
      status: "status" in parsed ? parsed.status : "confirmed",
      guest_notes: "guest_notes" in parsed ? parsed.guest_notes : null,
      cancelled_at: "cancelled_at" in parsed ? parsed.cancelled_at : null,
    };
    return managed;
  }
  return request<ManagedBooking>("/manage", { method: "POST", body: JSON.stringify({ manage_token: token }) });
}

export async function cancelBooking(token: string, reason: string) {
  if (!hasSupabaseConfig) {
    const booking = await getManagedBooking(token);
    const cancelled = { ...booking, status: "cancelled" as const, cancelled_at: new Date().toISOString() };
    localStorage.setItem(`rsm-booking:${token}`, JSON.stringify(cancelled));
    return cancelled;
  }
  return request<ManagedBooking>("/cancel", {
    method: "POST",
    body: JSON.stringify({ manage_token: token, reason }),
  });
}

export async function rescheduleBooking(token: string, newStartsAt: string) {
  if (!hasSupabaseConfig) {
    const booking = await getManagedBooking(token);
    const duration = new Date(booking.ends_at).getTime() - new Date(booking.starts_at).getTime();
    const rescheduled: ManagedBooking = {
      ...booking,
      starts_at: newStartsAt,
      ends_at: new Date(new Date(newStartsAt).getTime() + duration).toISOString(),
      status: "confirmed",
      cancelled_at: null,
    };
    localStorage.setItem(`rsm-booking:${token}`, JSON.stringify(rescheduled));
    return rescheduled;
  }
  return request<ManagedBooking>("/reschedule", {
    method: "POST",
    body: JSON.stringify({ manage_token: token, new_starts_at: newStartsAt }),
  });
}

export const defaultBookingRange = () => {
  const from = toDateKey(new Date(), browserTimeZone);
  return { from, to: addDaysToKey(from, 13) };
};
